import _ from 'lodash';
import React, {
  createContext,
  useContext,
  useMemo,
  PropsWithChildren
} from 'react';
import { useUserInfo, useSignUpFormConfig } from '../../hook';
import {
  getBundleData,
  FREE_EDITION_LABEL_KEY,
  getFeaturesList,
  SOCIAL_PROOF_TEXT_KEY_PREFIX,
  BundleData,
  getPageHeaderLabels,
  PageHeaderLabels
} from '../../utility';
import { getSocialProofImageURLs, getSocialProofProductLabel } from './utility';
import {
  ProductDataType,
  UserDataType,
  FeatureFlags,
  BundleContent
} from '../../model';
import { FirstImpressionsImageUrls } from './types';

export type FirstImpressionsContextProps = {
  readonly labels: {
    [key: string]: string;
  };
  readonly productData: { [key: string]: ProductDataType };
  readonly firstImpressionsImageUrls: FirstImpressionsImageUrls;
  readonly featureFlags: FeatureFlags;
};

export const FirstImpressionsContext =
  createContext<FirstImpressionsContextProps>({
    labels: {},
    productData: {},
    firstImpressionsImageUrls: {
      backgroundImage: '',
      jiraSoftware: {
        heroImage: '',
        socialProofImage: ''
      },
      jiraServiceDesk: {
        heroImage: '',
        socialProofImage: ''
      },
      confluence: {
        heroImage: '',
        socialProofImage: ''
      },
      jiraCore: {
        heroImage: '',
        socialProofImage: ''
      },
      teamCentral: {
        heroImage: '',
        socialProofImage: ''
      },
      beacon: {
        heroImage: '',
        socialProofImage: ''
      },
      bitbucket: {
        heroImage: '',
        socialProofImage: ''
      }
    },
    featureFlags: {}
  });

export function FirstImpressionsProvider({
  children,
  ...props
}: PropsWithChildren<FirstImpressionsContextProps>) {
  return (
    <FirstImpressionsContext.Provider value={props}>
      {children}
    </FirstImpressionsContext.Provider>
  );
}

export type ActiveFormView =
  | null
  | 'team-central-logged-out'
  | 'team-central-ineligible'
  | 'first-impressions-loading'
  | 'first-impressions-logged-out'
  | 'first-impressions-logged-in';

export type FirstImpressionsState = {
  readonly pageHeaderLabels: PageHeaderLabels;
  readonly firstImpressionsImageUrls: FirstImpressionsImageUrls;
  readonly bundleData: BundleData;
  readonly activeFormView: ActiveFormView;
  readonly userInfo?: UserDataType;
  readonly bundleContent: BundleContent;
};

function buildFirstImpressionsState(
  activeBundlesMap: { [key: string]: ProductDataType },
  labels: { [key: string]: string },
  firstImpressionsImageUrls: FirstImpressionsImageUrls,
  userInfo?: UserDataType,
  bundleName?: string,
  productKeys?: string,
  edition?: string
): FirstImpressionsState {
  const bundleData = getBundleData({
    activeBundlesMap,
    bundleName,
    productKeys,
    edition
  });

  const pageHeaderLabels = getPageHeaderLabels({
    labels,
    bundleData
  });

  const bundleContent = getBundleContent({
    labels,
    bundleData,
    firstImpressionsImageUrls
  });

  let activeFormView: ActiveFormView = null;

  if (!userInfo) {
    activeFormView = 'first-impressions-loading';
  } else if (!userInfo.isLoggedIn) {
    activeFormView = 'first-impressions-logged-out';
  } else {
    activeFormView = 'first-impressions-logged-in';
  }

  return {
    pageHeaderLabels,
    firstImpressionsImageUrls,
    bundleData,
    activeFormView,
    userInfo,
    bundleContent
  };
}

type GetBundleContentOptions = {
  readonly bundleData: BundleData;
  readonly labels: { [key: string]: string };
  readonly firstImpressionsImageUrls: FirstImpressionsImageUrls;
};

function getBundleContent({
  bundleData,
  labels,
  firstImpressionsImageUrls
}: GetBundleContentOptions): BundleContent {
  // if default bundleContent exists in bundleData, return that
  if (bundleData.bundleContent?.default) {
    return bundleData.bundleContent.default;
  }

  // otherwise compute bundleContent the old way
  const primaryProductKey = bundleData.products[0].productKey;

  const productNameKey = getSocialProofProductLabel(primaryProductKey);

  const socialProofTextKey = SOCIAL_PROOF_TEXT_KEY_PREFIX + productNameKey;
  const socialProofText = labels[socialProofTextKey];

  const socialProofImageURLs = getSocialProofImageURLs({
    productKey: primaryProductKey,
    firstImpressionsImageUrls
  });

  const featureList = getFeaturesList({
    labels,
    bundleData,
    featuresListEditionLabelKey: FREE_EDITION_LABEL_KEY.toLowerCase()
  });

  return {
    heroImage: socialProofImageURLs.heroImage,
    headline: socialProofText,
    socialProofImage: socialProofImageURLs.logoImage,
    featureList
  };
}

export function useFirstImpressionsState(): FirstImpressionsState {
  const { labels, productData, firstImpressionsImageUrls } = useContext(
    FirstImpressionsContext
  );

  const {
    bundle: bundleName,
    product: productKeys,
    edition
  } = useSignUpFormConfig();

  const userInfo = useUserInfo();

  return useMemo(
    () =>
      buildFirstImpressionsState(
        productData,
        labels,
        firstImpressionsImageUrls,
        userInfo,
        bundleName,
        productKeys,
        edition
      ),
    [
      productData,
      labels,
      firstImpressionsImageUrls,
      userInfo,
      bundleName,
      productKeys,
      edition
    ]
  );
}
